import { useAuthFetch, useGuardedEffect } from "@p0security/frontend-core";
import {
  ErrorDisplay,
  useFirestoreCollection,
} from "@p0security/frontend-core/react";
import type { AutoApprover } from "@p0security/shared/types/policy/types";
import { BaseOptionType, Select } from "@p0security/ui";
import { grafanaIrmTeamLabel } from "components/Integrations/GrafanaIrm/labels";
import {
  StyledFormItem,
  standardFormItemCols,
} from "components/common/forms/styles";
import { startCase } from "lodash";
import { useCallback, useMemo, useState } from "react";

type IncidentioSchedule = {
  id: string;
  name: string;
  timezone: string;
  hasPagingChain?: boolean;
};

const selectLabels: Record<AutoApprover, string> = {
  "grafana-irm": "team",
  incidentio: "schedule",
  pagerduty: "service",
};

export const IncidentioScheduleSelect: React.FC<{
  integration: AutoApprover;
  services: string[];
  onChange: (services: string[]) => void;
}> = ({ integration, services, onChange }) => {
  const [error, setError] = useState<string | undefined>();
  const authFetch = useAuthFetch(setError);

  useGuardedEffect(
    () => async () => {
      await authFetch(`integrations/${integration}/refresh`, {
        method: "POST",
      });
    },
    [integration, authFetch],
    setError
  );

  const { docs } = useFirestoreCollection<IncidentioSchedule>(
    `integrations/${integration}/${selectLabels[integration]}s`,
    { live: true, tenantAware: true }
  );

  const options = useMemo(
    () =>
      docs?.map((s) => (
        <Select.Option key={s.data.id}>
          {grafanaIrmTeamLabel(s.data)}
        </Select.Option>
      )) ?? [],
    [docs]
  );

  const label = selectLabels[integration];

  const filterSchedule = useCallback(
    (input: string, option?: BaseOptionType) =>
      option?.children
        ?.toString()
        .toLowerCase()
        ?.includes(input.toLowerCase()) ?? false,
    []
  );

  return (
    <>
      {error && <ErrorDisplay error={error} />}
      <StyledFormItem
        {...standardFormItemCols}
        label={`${startCase(label)}`}
        rules={[
          {
            required: true,
            message: `Please select at least one ${label}`,
          },
        ]}
      >
        <Select
          allowClear
          filterOption={filterSchedule}
          mode="multiple"
          onChange={onChange}
          placeholder={`Select a ${label}`}
          value={services}
        >
          {options}
        </Select>
      </StyledFormItem>
    </>
  );
};
